import React from 'react';
import ReactGA from 'react-ga';

import Step1Picker from './Step1Picker';
import GroupStep from './GroupStep';
import TiebreakerStep from './TiebreakerStep';
import Result from './Result';
import SurpriseResult from './SurpriseResult';
import { scoreS1, scoreS2 } from './scoring';
import { DESTINATIONS, TOWN_ORDER } from '../../data/exploreCommunities';

import './style.scss';

const PROGRESS = { step1: '10%', group: '38%', tiebreaker: '65%', result: '100%' };

// NOTE: class component (not hooks). react-static 6 has a nested copy of
// react-dom in its own node_modules, so hooks called during SSR hit React
// error #307 ("more than one copy of React"). Class state + lifecycle
// methods SSR cleanly with both copies.
class Quiz extends React.Component {
  state = {
    step: 'step1',
    selected: new Set(),
    group: '',
    answers: {},
    topMatches: [],
    needsParking: false,
    winners: [],
    showDevBar: false,
  };

  cardRef = React.createRef();

  componentDidMount() {
    // Dev bar only on localhost or when URL has ?dev=1; hidden in production.
    // Decided here (not in render) so SSR doesn't render it and we avoid a
    // hydration mismatch.
    if (typeof window === 'undefined') return;
    const { hostname, search } = window.location;
    const isDev =
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      /[?&]dev=1\b/.test(search);
    if (isDev) this.setState({ showDevBar: true });
  }

  componentDidUpdate(_prevProps, prevState) {
    if (prevState.step !== this.state.step && this.cardRef.current) {
      this.cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  toggleImage = (id) => {
    this.setState(({ selected }) => {
      const next = new Set(selected);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { selected: next };
    });
  };

  setGroup = (group) => this.setState({ group });

  handleS1Next = () => {
    const res = scoreS1(this.state.selected);
    this.setState({
      topMatches: res.topMatches,
      needsParking: res.needsParking,
      step: 'group',
    });
  };

  handleGroupNext = () => {
    if (!this.state.group) return;
    this.setState({ step: 'tiebreaker' });
  };

  handlePick = (q, val) => {
    this.setState(({ answers }) => ({ answers: { ...answers, [q]: val } }));
  };

  handleResult = () => {
    const { group, answers, needsParking } = this.state;
    const res = scoreS2({ group, answers, needsParking });
    const label =
      res.winners[0] === '__surprise__' ? 'Surprise Me' : res.winners[0];
    ReactGA.event({ category: 'Quiz', action: 'Result', label });
    this.setState({
      winners: res.winners,
      needsParking: res.needsParking,
      step: 'result',
    });
  };

  backToStep1 = () => this.setState({ group: '', step: 'step1' });

  backToGroup = () => this.setState({ answers: {}, step: 'group' });

  retake = () => {
    this.setState({
      selected: new Set(),
      group: '',
      answers: {},
      topMatches: [],
      needsParking: false,
      winners: [],
      step: 'step1',
    });
  };

  // TEMP: dev jump straight to a result card (for image-asset iteration).
  // Remove the `<dev jump bar />` block in render() + this method when done.
  devJumpTo = (dest) => {
    this.setState({
      winners: [dest],
      // pick a sensible default group so the result card renders fully
      group: this.state.group || 'adults',
      answers: { 1: 'mix', 2: 'nice', 3: 'car' },
      needsParking: true,
      step: 'result',
    });
  };

  devSetGroup = (e) => this.setState({ group: e.target.value });

  render() {
    const {
      step,
      selected,
      group,
      answers,
      topMatches,
      needsParking,
      winners,
    } = this.state;
    const isSurprise =
      winners[0] === '__surprise__' || !DESTINATIONS[winners[0]];

    return (
      <div className="fif-quiz">
        {/* Dev bar — only on localhost / ?dev=1; hidden in production. */}
        {this.state.showDevBar && (
          <div className="fif-quiz-devbar" role="region" aria-label="Dev controls">
            <span className="devbar-label">DEV · jump to result</span>
            {TOWN_ORDER.map((name) => (
              <button
                type="button"
                key={name}
                className="devbar-btn"
                onClick={() => this.devJumpTo(name)}
              >
                {name}
              </button>
            ))}
            <button
              type="button"
              className="devbar-btn devbar-btn-surprise"
              onClick={() => this.devJumpTo('__surprise__')}
            >
              ✦ Surprise Me
            </button>
            <span className="devbar-sep" />
            <label className="devbar-group">
              Group:&nbsp;
              <select value={group} onChange={this.devSetGroup}>
                <option value="">—</option>
                <option value="kids">kids</option>
                <option value="teens">teens</option>
                <option value="adults">adults</option>
                <option value="mixed">mixed</option>
                <option value="solo">solo</option>
              </select>
            </label>
            <button
              type="button"
              className="devbar-btn devbar-btn-reset"
              onClick={this.retake}
            >
              ↺ Reset to Step 1
            </button>
          </div>
        )}

        <div className="quiz-card" ref={this.cardRef}>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: PROGRESS[step] }}
            />
          </div>
          <div className="quiz-inner">
            {step === 'step1' && (
              <Step1Picker
                selected={selected}
                onToggle={this.toggleImage}
                onNext={this.handleS1Next}
              />
            )}
            {step === 'group' && (
              <GroupStep
                group={group}
                onPick={this.setGroup}
                onBack={this.backToStep1}
                onNext={this.handleGroupNext}
              />
            )}
            {step === 'tiebreaker' && (
              <TiebreakerStep
                topMatches={topMatches}
                answers={answers}
                onPick={this.handlePick}
                onBack={this.backToGroup}
                onResult={this.handleResult}
              />
            )}
            {step === 'result' &&
              (isSurprise ? (
                <SurpriseResult onRetake={this.retake} />
              ) : (
                <Result
                  dest={winners[0]}
                  winners={winners}
                  group={group}
                  answers={answers}
                  needsParking={needsParking}
                  onRetake={this.retake}
                />
              ))}
          </div>
        </div>
      </div>
    );
  }
}

export default Quiz;
